import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { GALLERY_IMAGES } from '../data/siteData';
import { API } from '../config/api';
import axios from 'axios';

export default function Photos() {
  const [filter, setFilter] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [dynamicPhotos, setDynamicPhotos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gallery-photos`).then((res) => {
      const mapped = res.data.map((p) => ({
        url: `${API}/photos/serve/${p.id}`,
        caption: p.caption || p.original_filename,
        category: p.category,
      }));
      setDynamicPhotos(mapped);
    }).catch(() => {});
  }, []);

  const allImages = [...GALLERY_IMAGES, ...dynamicPhotos];

  const categories = ['All', ...new Set(allImages.map((img) => img.category))];

  const filtered =
    filter === 'All'
      ? allImages
      : allImages.filter((img) => img.category === filter);

  const openLightbox = (i) => { setLightboxIndex(i); setLightboxOpen(true); };
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % filtered.length);

  return (
    <div data-testid="photos-page">
      {/* Hero */}
      <section className="bg-sky-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">Our Work</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-black text-slate-900 mb-4">
            Photo Gallery
          </h1>
          <p className="text-slate-500 text-base max-w-lg mx-auto">
            Browse examples of our pressure washing results across Southwest Florida.
          </p>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10 justify-center" data-testid="photo-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`photo-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  filter === cat
                    ? 'bg-sky-700 text-white shadow-md'
                    : 'bg-sky-50 text-slate-600 hover:bg-sky-100 border border-sky-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="photo-gallery-grid">
            {filtered.map((img, i) => (
              <button
                key={i}
                onClick={() => openLightbox(i)}
                data-testid={`gallery-image-${i}`}
                className="gallery-item rounded-2xl overflow-hidden h-64 cursor-pointer border border-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-400"
              >
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">No photos found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox with navigation */}
      {lightboxOpen && filtered.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
          >
            <X className="w-7 h-7" />
          </button>

          {filtered.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                className="absolute left-4 text-white/70 hover:text-white p-2 bg-black/30 rounded-full z-10"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                className="absolute right-4 text-white/70 hover:text-white p-2 bg-black/30 rounded-full z-10"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[85vh] px-4">
            <img
              src={filtered[lightboxIndex]?.url}
              alt={filtered[lightboxIndex]?.caption || ''}
              className="max-w-full max-h-[80vh] object-contain rounded-xl mx-auto"
            />
            {filtered[lightboxIndex]?.caption && (
              <p className="text-white/70 text-sm text-center mt-3">{filtered[lightboxIndex].caption}</p>
            )}
            {filtered.length > 1 && (
              <p className="text-white/40 text-xs text-center mt-1">{lightboxIndex + 1} / {filtered.length}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
